import { useEffect, useState } from "react";
import { NavLink, useParams } from "react-router";

import * as Api from "@/api";
import trueBlue from "@/design/colors/trueBlue";
import Avatar from "@/design/components/Avatar";
import HeaderCard from "@/design/components/HeaderCard";
import FormGroup from "@/design/forms/FormGroup";
import StringForm from "@/design/forms/StringForm";
import useAccessibleColors from "@/utils/hooks/useAccessibleColors";
import { toggleLoadingState } from "@/utils/utility";
import Valkyrie, { viArrowLeft, viCheck, viPhoneOffice, viXmark } from "@sippy-platform/valkyrie";

import {
  Alert,
  CardHeader,
  Chip,
  Container,
  Grid,
  IconButton,
  Avatar as MuiAvatar,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import LineKeyIcon from "../../domain/settings/linekeytemplates/lineKeyEditor/LineKeyIcon";

function Device() {
  const { id } = useParams();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("md"));
  const colors = useAccessibleColors();

  const [device, setDevice] = useState({ type: "", lineKeyTypes: [] });

  // Get general data
  useEffect(() => {
    toggleLoadingState(true);

    Promise.resolve(Api.getDeviceById({ id: Number(id) }))
      .then((response) => setDevice(response.data.device))
      .finally(() => toggleLoadingState(false));
  }, [id]);

  return (
    <Container>
      <title>{`${device.type ? `${device.brand} ${device.type}` : "Device"} \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>
      <Stack
        sx={{
          gap: 2,
        }}
      >
        <HeaderCard>
          <CardHeader
            avatar={
              !matches ? (
                <IconButton variant="avatar" component={NavLink} to={".."}>
                  <Valkyrie icon={viArrowLeft} /> <Avatar text={device.type} icon={viPhoneOffice} />
                </IconButton>
              ) : (
                <Avatar text={device.type} icon={viPhoneOffice} />
              )
            }
            title={device.type || "No device selected"}
          />
        </HeaderCard>
        <FormGroup title="General">
          {device.brand && (
            <Grid size={12}>
              <img src={device.imageUrl} style={{ width: 192, display: "block", margin: "0 auto" }} alt={device.type} />
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <StringForm label="Brand" text={device.brand} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <StringForm label="Model" text={device.type} />
          </Grid>
        </FormGroup>
        {device.endOfLife && (
          <Alert severity="warning" sx={{ boxShadow: 1 }}>
            This device is no longer supported by its manufacturer and won't receive any future updates. Sippy will
            continue to support this device.
          </Alert>
        )}
        <FormGroup title="Device capabilities">
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <StringForm label="Phonebooks" text={device.phonebooks} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <StringForm label="Accounts" text={device.accounts} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <StringForm label="Line keys" text={device.lineKeys} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <StringForm label="Line key pages" text={device.lineKeyPages} />
          </Grid>
          {device.lineKeyTypes.length >= 1 && (
            <Grid size={12}>
              <StringForm
                label="Line key types"
                action={
                  <span id="types">
                    {device.lineKeyTypes.map((types) => (
                      <Chip
                        key={types.id}
                        label={types.type}
                        avatar={
                          <MuiAvatar
                            sx={[
                              {
                                width: 32,
                                height: 32,
                                fontSize: 32 / 2,
                                fontWeight: 600,
                                lineHeight: `${32}px`,
                                color: `#fff !important`,
                                background: `${trueBlue[500]} !important`,
                              },
                            ]}
                          >
                            <LineKeyIcon lineKeyId={types.lineKeyId} />
                          </MuiAvatar>
                        }
                      />
                    ))}
                  </span>
                }
              />
            </Grid>
          )}
        </FormGroup>
        <FormGroup title="Feature support">
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <StringForm
              label="Missed call log reporting"
              text={
                device.missedCallLog ? (
                  <Valkyrie icon={viCheck} style={{ color: colors.green }} />
                ) : (
                  <Valkyrie icon={viXmark} style={{ color: colors.red }} />
                )
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <StringForm
              label="Call waiting"
              text={
                device.callWaiting === "yes" ? (
                  <Valkyrie icon={viCheck} style={{ color: colors.green }} />
                ) : (
                  <Valkyrie icon={viXmark} style={{ color: colors.red }} />
                )
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <StringForm
              label="Ringtone"
              text={
                device.ringtone ? (
                  <Valkyrie icon={viCheck} style={{ color: colors.green }} />
                ) : (
                  <Valkyrie icon={viXmark} style={{ color: colors.red }} />
                )
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <StringForm
              label="Distinctive Ringtone"
              text={
                device.distinctiveRingtone ? (
                  <Valkyrie icon={viCheck} style={{ color: colors.green }} />
                ) : (
                  <Valkyrie icon={viXmark} style={{ color: colors.red }} />
                )
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <StringForm
              label="Flex Desking"
              text={
                device.flexDesk === "yes" ? (
                  <Valkyrie icon={viCheck} style={{ color: colors.green }} />
                ) : (
                  <Valkyrie icon={viXmark} style={{ color: colors.red }} />
                )
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <StringForm
              label="Handset volume"
              text={
                device.handsetSendVolume === "yes" ? (
                  <Valkyrie icon={viCheck} style={{ color: colors.green }} />
                ) : (
                  <Valkyrie icon={viXmark} style={{ color: colors.red }} />
                )
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <StringForm
              label="Semi-attended transfer"
              text={
                device.semiAttendedTransfer ? (
                  <Valkyrie icon={viCheck} style={{ color: colors.green }} />
                ) : (
                  <Valkyrie icon={viXmark} style={{ color: colors.red }} />
                )
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <StringForm
              label="Allow DND"
              text={
                device.allowDND === "yes" ? (
                  <Valkyrie icon={viCheck} style={{ color: colors.green }} />
                ) : (
                  <Valkyrie icon={viXmark} style={{ color: colors.red }} />
                )
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <StringForm
              label="Line Key Length"
              text={
                device.dsskeyLength === "yes" ? (
                  <Valkyrie icon={viCheck} style={{ color: colors.green }} />
                ) : (
                  <Valkyrie icon={viXmark} style={{ color: colors.red }} />
                )
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <StringForm
              label="Wallpaper"
              text={
                device.wallpaper === "yes" ? (
                  <Valkyrie icon={viCheck} style={{ color: colors.green }} />
                ) : (
                  <Valkyrie icon={viXmark} style={{ color: colors.red }} />
                )
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <StringForm
              label="Screensaver"
              text={
                device.screensaver ? (
                  <Valkyrie icon={viCheck} style={{ color: colors.green }} />
                ) : (
                  <Valkyrie icon={viXmark} style={{ color: colors.red }} />
                )
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <StringForm
              label="Action URI"
              text={
                device.actionURI ? (
                  <Valkyrie icon={viCheck} style={{ color: colors.green }} />
                ) : (
                  <Valkyrie icon={viXmark} style={{ color: colors.red }} />
                )
              }
            />
          </Grid>
        </FormGroup>
      </Stack>
    </Container>
  );
}

export default Device;
